/**
 * Redaction layer for everything speedchainr2 sends to Sentry (SEE-1087).
 *
 * Network traffic (XHR/fetch breadcrumbs, replay network capture, error
 * events) is logged, but values under sensitive keys and sensitive-looking
 * string patterns are masked so the rest of the payload stays visible.
 *
 * SEE-1834 tightened it for unmasked session replays: emails and identity-route
 * segments in strings, contact PII (names, emails, phones, person addresses) by
 * key, and network bodies that are deep, truncated or carry nested JSON.
 */
const REDACTED = '[Redacted]';

const SENSITIVE_KEY_RE =
  /pass(word|code)|secret|(?:auth|access|id|refresh|session)?token|authorization|api[-_]?key|credential|ssn|social[-_]?security|tax[-_]?id|account[-_]?number|routing[-_]?number|card[-_]?number|\bpan\b|cvv|cvc|\bpin\b|date[-_]?of[-_]?birth|\bdob\b|signature/i;

const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
const JWT_RE = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;
const BEARER_RE = /\b(Bearer\s+)[\w.~+/-]+/gi;
// 13-19 digit runs (optionally space/dash separated); Luhn-checked before masking.
const PAN_CANDIDATE_RE = /\b(?:\d[ -]?){12,18}\d\b/g;
const SENSITIVE_QUERY_PARAM_RE =
  /([?&](?:X-Amz-(?:Signature|Credential|Security-Token)|token|access_token|id_token|code|procore_code|key)=)[^&\s]+/gi;
// The `:email`/`:username` segment of the identity routes (`RoutingTable.tsx`,
// `MobileRoutingTable.tsx`). A username is often not an email, so the email
// pattern alone would miss it. Runs before EMAIL_RE so the route survives.
const IDENTITY_ROUTE_SEGMENT_RE = /(\/(?:register|verify-user|forgot-password)\/)[^/?#&\s"'<>]+/g;
// Plain or percent-encoded (`a%2Bx%40b.com`). Bounded, because strings run up to
// 150 KB and an unbounded local part backtracks on long `@`-free runs. No
// lookbehind: older iOS WebViews throw at module load. `/ ? = &` stay out of the
// local part, or it swallows the URL in front of the address; so do their
// encodings and `%20`, which is why only encoded address characters are allowed.
// A leading `%XX` is captured and put back: without it, `User%20a%40b.com`
// would start matching at the `20` (word characters) and eat half the escape.
const EMAIL_RE =
  /(%[0-9A-Fa-f]{2})?(?:[\w.!#$'*+^`{|}~-]|%(?:2[13479AaBbDdEe]|5[EeFf]|60|7[B-Eb-e])){1,64}(?:@|%40)(?:[A-Za-z0-9-]{1,63}\.){1,8}[A-Za-z]{2,24}/g;
// Click-breadcrumb messages copy these attributes from the live DOM, unmasked.
const UI_BREADCRUMB_ATTRIBUTE_RE = /\[(aria-label|title|alt)="[^"]*"\]/g;

/**
 * Contact-PII keys, matched exactly on the normalised key (lower-cased, `_`/`-`
 * stripped) and only for string values, so `requireAddress: true`,
 * `yearsAtCurrentAddress: 3` and `ipAddress` stay visible.
 */
const CONTACT_PII_KEYS = new Set([
  'firstname',
  'lastname',
  'middlename',
  'givenname',
  'familyname',
  'cardholdername',
  'individualname',
  'contactname',
  'addressfirstname',
  'addresslastname',
  'username',
  'preferredusername',
  'email',
  'emailaddress',
  'emailaddresses',
  'phone',
  'phonenumber',
  'phonenumbervalue',
  'mobilephone',
  'birthdate',
  'createdby',
]);
/** Redacted only in person context: the same keys name a company or an ERP label elsewhere. */
const PERSON_CONTEXT_KEYS = new Set([
  'name',
  'displayname',
  'fullname',
  'label',
  'street',
  'street1',
  'street2',
  'streetaddress',
  'extendedaddress',
  'line1',
  'line2',
  'address1',
  'address2',
  'addressstreet1',
  'addressstreet2',
  'addresszip',
  'addresscity',
  'postalcode',
  'zip',
  'zipcode',
  'homeaddress',
]);
/** Parent keys whose object values describe a person (or a card, whose name defaults to one). */
const PERSON_PARENT_KEYS = new Set([
  'user',
  'users',
  'paidoluser',
  'paidolusers',
  'cardholder',
  'cardholders',
  'defaultagaveemployee',
  'employee',
  'primaryauthorizedperson',
  'beneficialowner',
  'beneficialowners',
  'administrator',
  'person',
  'paymentcard',
  'paymentcards',
  'homeaddress',
  'phonenumbers',
  // Filter options whose `label` is a person or card name.
  'userid',
  'cardnames',
  'groupadmin',
  'groupadmins',
]);
/** Parent keys that leave person context: a person's company or group is not a person. */
const BUSINESS_PARENT_KEYS = new Set([
  'paidol',
  'company',
  'business',
  'vendor',
  'vendors',
  'merchant',
  'bank',
  'cardgroup',
  'cardgroups',
  'projectgroup',
  'companyaddress',
  'legaladdress',
]);
const PERSON_MARKER_KEYS = ['firstname', 'givenname', 'familyname'];
/** One-time codes, always redacted. */
const OTP_KEYS = new Set(['otp', 'otpcode', 'verificationcode', 'confirmationcode', 'mfacode']);
/**
 * A bare `code` is an OTP only beside an identity key (`confirmSignUp({ username, code })`);
 * elsewhere it's a cost code, an error code or a currency, and stays visible.
 */
const AUTH_IDENTITY_KEYS = ['username', 'email', 'phone', 'phonenumber'];

/** JSON has no cycles, so network bodies can go deeper than events. */
const NETWORK_BODY_DEPTH = 32;

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = digits.charCodeAt(i) - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

export function isSensitiveSentryKey(key: string): boolean {
  return SENSITIVE_KEY_RE.test(key);
}

/** Mask sensitive patterns inside a free-form string (URLs, messages, bodies). */
export function redactString(value: string): string {
  return value
    .replace(IDENTITY_ROUTE_SEGMENT_RE, `$1${REDACTED}`)
    .replace(EMAIL_RE, `$1${REDACTED}`)
    .replace(SSN_RE, REDACTED)
    .replace(JWT_RE, REDACTED)
    .replace(BEARER_RE, `$1${REDACTED}`)
    .replace(SENSITIVE_QUERY_PARAM_RE, `$1${REDACTED}`)
    .replace(PAN_CANDIDATE_RE, (match) => {
      const digits = match.replace(/[ -]/g, '');
      return passesLuhn(digits) ? REDACTED : match;
    });
}

/** Click-breadcrumb `message`: scrub the attribute values `htmlTreeAsString` copied from the DOM. */
export function redactUiBreadcrumbMessage(message: string): string {
  return redactString(message.replace(UI_BREADCRUMB_ATTRIBUTE_RE, `[$1="${REDACTED}"]`));
}

const normaliseKey = (key: string): string => key.toLowerCase().replace(/[_-]/g, '');

type PersonContext = 'none' | 'person' | 'phone';

interface RedactState {
  seen: WeakSet<object>;
  /** Return `[Redacted]` for objects past the depth limit instead of passing them through raw. */
  failClosed: boolean;
}

function looksLikeJson(value: string): boolean {
  return value.startsWith('{') || value.startsWith('[');
}

function childContext(key: string, context: PersonContext): PersonContext {
  const normalised = normaliseKey(key);
  if (normalised === 'phonenumbers') return 'phone';
  if (PERSON_PARENT_KEYS.has(normalised)) return 'person';
  if (BUSINESS_PARENT_KEYS.has(normalised)) return 'none';
  return context;
}

function isContactPiiKey(key: string, context: PersonContext): boolean {
  const normalised = normaliseKey(key);
  if (CONTACT_PII_KEYS.has(normalised)) return true;
  if (context === 'none') return false;
  return PERSON_CONTEXT_KEYS.has(normalised) || (context === 'phone' && normalised === 'number');
}

function redactContactValue(value: unknown): unknown {
  if (typeof value === 'string') return REDACTED;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string'))
    return value.map(() => REDACTED);
  return undefined;
}

function redactValue(
  value: unknown,
  depth: number,
  context: PersonContext,
  state: RedactState,
  inNestedJson = false
): unknown {
  if (typeof value === 'string') {
    // AWSJSON fields arrive as JSON strings; redact their contents too (once, not recursively).
    if (!inNestedJson && looksLikeJson(value)) {
      const parsed = parseJsonValue(value);
      if (parsed !== undefined) return JSON.stringify(redactValue(parsed, depth, context, state, true));
    }
    return redactString(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth <= 0) {
    return state.failClosed ? REDACTED : value;
  }
  if (state.seen.has(value)) {
    return undefined;
  }
  state.seen.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth - 1, context, state, inNestedJson));
  }
  const entries = Object.entries(value as Record<string, unknown>);
  const ownContext =
    context === 'none' && entries.some(([key]) => PERSON_MARKER_KEYS.includes(normaliseKey(key)))
      ? 'person'
      : context;
  const isAuthPayload = entries.some(([key]) => AUTH_IDENTITY_KEYS.includes(normaliseKey(key)));
  const out: Record<string, unknown> = {};
  entries.forEach(([key, val]) => {
    const normalised = normaliseKey(key);
    if (isSensitiveSentryKey(key) || OTP_KEYS.has(normalised) || (isAuthPayload && normalised === 'code')) {
      out[key] = REDACTED;
      return;
    }
    const contactValue = isContactPiiKey(key, ownContext) ? redactContactValue(val) : undefined;
    out[key] =
      contactValue !== undefined
        ? contactValue
        : redactValue(val, depth - 1, childContext(key, ownContext), state, inNestedJson);
  });
  return out;
}

/**
 * Return a copy of `value` with sensitive keys and contact PII masked and string
 * values pattern-scrubbed. Depth-limited and cycle-safe.
 */
export function deepRedact<T>(value: T, depth = 8, seen = new WeakSet<object>()): T {
  return redactValue(value, depth, 'none', { seen, failClosed: false }) as T;
}

/** `deepRedact` for network bodies: deeper, and objects past the limit are redacted, not kept. */
function deepRedactNetworkBody(value: unknown): unknown {
  return redactValue(value, NETWORK_BODY_DEPTH, 'none', { seen: new WeakSet<object>(), failClosed: true });
}

// A `"key": "value"` pair in JSON text. The value may be cut off at the
// truncation point, so the closing quote is optional at end of input.
const JSON_STRING_PAIR_RE = /"((?:[^"\\]|\\.){1,64})"(\s*:\s*)"(?:[^"\\]|\\.)*(?:"|$)/g;

/**
 * Bodies over the SDK's 150 KB limit arrive as truncated JSON text that can't be
 * parsed. Redact what can be recognised without parsing: string values under
 * sensitive and always-redacted contact keys.
 */
function redactTruncatedJson(body: string): string {
  return body.replace(JSON_STRING_PAIR_RE, (match: string, key: string, separator: string) =>
    isSensitiveSentryKey(key) || CONTACT_PII_KEYS.has(normaliseKey(key))
      ? `"${key}"${separator}"${REDACTED}"`
      : match
  );
}

interface GraphQLBody {
  operationName?: string;
  query?: string;
  variables?: Record<string, unknown>;
}

function parseJsonValue(value: string): object | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function parseJsonBody(body: unknown): GraphQLBody | null {
  if (typeof body !== 'string' || !looksLikeJson(body)) return null;
  return (parseJsonValue(body) as GraphQLBody | undefined) ?? null;
}

/** Redact a network body that may be a JSON string, parsed JSON, or plain text. */
function redactBody(body: unknown): unknown {
  if (typeof body === 'string') {
    const parsed = parseJsonBody(body);
    if (parsed) return JSON.stringify(deepRedactNetworkBody(parsed));
    return redactString(looksLikeJson(body) ? redactTruncatedJson(body) : body);
  }
  return deepRedactNetworkBody(body);
}

/** Best-effort GraphQL operation name from a request body. */
export function getGraphQLOperation(body: unknown): string | undefined {
  const parsed = parseJsonBody(body);
  if (!parsed || typeof parsed.query !== 'string') return undefined;
  if (parsed.operationName) return parsed.operationName;
  const match = parsed.query.match(/\b(query|mutation|subscription)\s+(\w+)/);
  return match ? match[2] : parsed.query.match(/{\s*(\w+)/)?.[1];
}

function requestBodyFromHint(breadcrumb: SentryBreadcrumb, hint?: BreadcrumbHint): unknown {
  if (breadcrumb.category === 'xhr') return hint?.input;
  const fetchArgs = hint?.input;
  return Array.isArray(fetchArgs) ? fetchArgs[1]?.body : undefined;
}

interface SentryBreadcrumb {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
}

interface BreadcrumbHint {
  input?: unknown;
  [key: string]: unknown;
}

/**
 * `beforeBreadcrumb` hook: tag GraphQL calls with their operation name (every
 * AppSync request shares one URL, so breadcrumbs are useless without it) and
 * scrub URLs/messages/data of sensitive values.
 */
export function processBreadcrumb(breadcrumb: SentryBreadcrumb, hint?: BreadcrumbHint): SentryBreadcrumb {
  if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
    const operation = getGraphQLOperation(requestBodyFromHint(breadcrumb, hint));
    if (operation && breadcrumb.data) {
      breadcrumb.data.graphql_operation = operation;
    }
    if (breadcrumb.data) {
      breadcrumb.data = deepRedact(breadcrumb.data);
    }
    return breadcrumb;
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = breadcrumb.category?.startsWith('ui.')
      ? redactUiBreadcrumbMessage(breadcrumb.message)
      : redactString(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = deepRedact(breadcrumb.data);
  }
  return breadcrumb;
}

interface SentryEventLike {
  transaction?: string;
  request?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: SentryBreadcrumb[];
  message?: string;
  exception?: { values?: Array<{ value?: string }> };
  spans?: Array<{ description?: string; data?: Record<string, unknown> }>;
  [key: string]: unknown;
}

/** `beforeSend`/`beforeSendTransaction` hook: scrub the payload sections that carry app data. */
export function redactEvent<T extends SentryEventLike>(event: T): T {
  if (event.request) event.request = deepRedact(event.request);
  if (event.extra) event.extra = deepRedact(event.extra);
  if (event.contexts) event.contexts = deepRedact(event.contexts);
  if (typeof event.message === 'string') event.message = redactString(event.message);
  // The transaction name (and so the issue culprit) can be a raw identity-route path.
  if (typeof event.transaction === 'string') event.transaction = redactString(event.transaction);
  event.exception?.values?.forEach((item) => {
    if (typeof item.value === 'string') item.value = redactString(item.value);
  });
  event.spans?.forEach((span) => {
    if (typeof span.description === 'string') span.description = redactString(span.description);
    if (span.data) span.data = deepRedact(span.data);
  });
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => processBreadcrumb(crumb));
  }
  return event;
}

interface ReplayPerformanceEntry {
  data?: {
    tag?: string;
    payload?: {
      op?: string;
      description?: string;
      data?: {
        request?: { body?: unknown; headers?: Record<string, unknown> };
        response?: { body?: unknown; headers?: Record<string, unknown> };
        [key: string]: unknown;
      };
    };
  };
  [key: string]: unknown;
}

/**
 * Replay `beforeAddRecordingEvent` hook: scrub captured network request and
 * response bodies (enabled by `networkDetailAllowUrls`) before they upload.
 */
export function redactReplayEvent<T extends ReplayPerformanceEntry>(event: T): T {
  const payload = event.data?.tag === 'performanceSpan' ? event.data.payload : undefined;
  if (!payload?.op?.startsWith('resource.')) return event;
  if (payload.description) {
    payload.description = redactString(payload.description);
  }
  if (payload.data?.request?.headers) {
    payload.data.request.headers = deepRedact(payload.data.request.headers);
  }
  if (payload.data?.response?.headers) {
    payload.data.response.headers = deepRedact(payload.data.response.headers);
  }
  if (payload.data?.request?.body !== undefined) {
    payload.data.request.body = redactBody(payload.data.request.body);
  }
  if (payload.data?.response?.body !== undefined) {
    payload.data.response.body = redactBody(payload.data.response.body);
  }
  return event;
}

interface ReplayEventLike {
  type?: string;
  urls?: unknown[];
  request?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Global-scope event processor for the replay event (`type: 'replay_event'`).
 * `beforeSend` never sees it, and it carries the replay's `urls` list plus the
 * `request.url` and `Referer` header the HttpContext integration adds — any of
 * which can be an identity route with an email in it. Always returns the event:
 * `null` from a processor drops the whole replay segment.
 */
export function redactReplayEventEnvelope<T extends ReplayEventLike>(event: T): T {
  if (event.type !== 'replay_event') return event;
  try {
    if (Array.isArray(event.urls)) {
      event.urls = event.urls.map((url) => (typeof url === 'string' ? redactString(url) : url));
    }
  } catch {
    event.urls = [];
  }
  try {
    if (event.request) event.request = deepRedact(event.request);
  } catch {
    delete event.request;
  }
  return event;
}
